<template>
  <!-- Need to add height inherit because Vue 2 don't support multiple root ele -->
    <div style="height: inherit" class="setup nominee-director">
        <table-and-header
            ref="table"
            :nominee-director-list= "nomineeDirectors"
            :is-fetching-data= "isFetchingData"
            :data-delete-action-loading="isButtonActionLoading"
            @addNomineeDirector = "handleNomineeDirectorCreateOrEdit"
            @editNomineeDirector="handleNomineeDirectorCreateOrEdit"
            @removeNomineeDirector="removeNomineeDirector"
        />

        <create-or-edit
            ref="createOrEdit"
            v-model="isNomineeDirectorHandlerSidebarActive"
            :nominee-director="nomineeDirector"
            :clear-nominee-director-data="clearNomineeDirectorData"
            :data-sidebar-action-loading="isButtonActionLoading"
            :data-auto-fill-requested-nominee-director="autoFillRequestedNomineeDirector"
            @add-nominee-director="addNomineeDirector"
            @update-nominee-director="updateNomineeDirector"
            @request-autofill-nominee-director-data="requestAutofillNomineeDirectorData"
        />
    </div>
</template>

<script>
import {
    ref, watch, computed,
} from '@vue/composition-api';

import { formatDate, avatarText } from '@core/utils/filter';

import { useRouter } from '@core/utils/utils';
import NomineeDirector from '@/models/NomineeDirector';
import { errorToast, successToast } from "@/views/helpers/toast";
import { SOMETHING_WENT_WRONG } from '@/constants/errors';
import User from '@/models/User';
import {
    NOMINEE_DIRECTOR_CREATE_SUCCESS,
    NOMINEE_DIRECTOR_UPDATE_SUCCESS,
    NOMINEE_DIRECTOR_DELETE_SUCCESS
} from '@/constants/success';
import TableAndHeader from './TableAndHeader.vue';
import CreateOrEdit from './CreateOrEdit.vue';

export default {
    components: {
        // App SFC
        CreateOrEdit,
        TableAndHeader,
    },

    setup() {
        const { route, router } = useRouter();
        const routeParams = computed(() => route.value.params);

        watch(routeParams, () => {
            // eslint-disable-next-line no-use-before-define
            fetchNomineeDirector();
        });

        const nomineeDirectors = ref([]);
        const isFetchingData = ref(false);
        const isNomineeDirectorHandlerSidebarActive = ref(false);
        const isButtonActionLoading = ref(false);
        const table = ref(null);
        const autoFillRequestedNomineeDirector = ref({});

        const nomineeDirectorBluePrint = {
            id                  : undefined,
            name                : undefined,
            idType              : undefined,
            NRIC                : undefined,
            dateOfExpiry        : undefined,
            address             : undefined,
            DOB                 : undefined,
            nationality         : undefined,
            email               : undefined,
            contact_number      : undefined,
        };

        const nomineeDirector = ref(JSON.parse(JSON.stringify(nomineeDirectorBluePrint)));

        const clearNomineeDirectorData = () => {
            nomineeDirector.value = JSON.parse(JSON.stringify(nomineeDirectorBluePrint));
        };

        const addNomineeDirector = newNomineeDirector => {
            isButtonActionLoading.value = true;

            NomineeDirector.create(newNomineeDirector)
                .then( payload => {
                    // eslint-disable-next-line no-use-before-define
                    const data = setNomineeDirector(payload);
                    nomineeDirectors.value.push(data);

                    successToast(NOMINEE_DIRECTOR_CREATE_SUCCESS);
                    isNomineeDirectorHandlerSidebarActive.value = false;
                    isButtonActionLoading.value = false;
                })
                .catch(e => {
                    errorToast(SOMETHING_WENT_WRONG);
                    isButtonActionLoading.value = false;

                    console.error(
                        "error while trying to store nominee director in setup",
                        e
                    );
                });

            setTimeout(()=>{
                clearNomineeDirectorData();
            },1000);
        };

        const removeNomineeDirector = deleteNomineeDirector => {
            isButtonActionLoading.value = true;

            NomineeDirector.delete(deleteNomineeDirector.item.id, deleteNomineeDirector.item)
                .then(() => {
                    nomineeDirectors.value = nomineeDirectors.value.filter( exitingNomineeDirector =>
                        // eslint-disable-next-line no-use-before-define
                        exitingNomineeDirector.id !== deleteNomineeDirector.item.id
                    );

                    successToast(NOMINEE_DIRECTOR_DELETE_SUCCESS);
                    table.value.$refs.modal.$refs.confirmationModal.hide();
                    isButtonActionLoading.value = false;
                })
                .catch(e => {
                    errorToast(SOMETHING_WENT_WRONG);
                    isButtonActionLoading.value = false;

                    console.error(
                        "error while trying to delete nominee director in setup",
                        e
                    );
                });
        };

        const updateNomineeDirector = nomineeDirectorUpdate => {
            isButtonActionLoading.value = true;

            NomineeDirector
                .update(nomineeDirectorUpdate.id, nomineeDirectorUpdate)
                .then(() => {
                    nomineeDirectors.value.forEach(existingNomineeDirector => {
                        if (existingNomineeDirector.id === nomineeDirectorUpdate.id) {
                            Object.keys(nomineeDirectorBluePrint).forEach(key => {
                                if (key === 'nominee_type')
                                    // eslint-disable-next-line no-param-reassign
                                    existingNomineeDirector.nominee_type = nomineeDirectorUpdate.nominee_type.value;
                                else
                                    // eslint-disable-next-line no-param-reassign
                                    existingNomineeDirector[key] = nomineeDirectorUpdate[key];
                            });
                        }
                    });

                    successToast(NOMINEE_DIRECTOR_UPDATE_SUCCESS);
                    isNomineeDirectorHandlerSidebarActive.value = false;
                    isButtonActionLoading.value = false;
                })
                .catch(e => {
                    errorToast(SOMETHING_WENT_WRONG);
                    isButtonActionLoading.value = false;

                    console.error(
                        "error while trying to update nominee director in setup",
                        e
                    );
                });
        };

        const perfectScrollbarSettings = {
            maxScrollbarLength: 150,
        };

        // eslint-disable-next-line no-use-before-define
        const updateRouteQuery = val => {
            const currentRouteQuery = JSON.parse(JSON.stringify(route.value.query));

            if (val) currentRouteQuery.q = val;
            else delete currentRouteQuery.q;

            router.replace({ name: route.name, query: currentRouteQuery });
        };

        const setNomineeDirector = dataNomineeDirector => ({
            id : dataNomineeDirector.id,
            name : dataNomineeDirector?.user_info?.legal_name ?? dataNomineeDirector.name,
            idType: dataNomineeDirector?.user_info?.id_type
                ?{
                    id: dataNomineeDirector?.user_info.id_type?.id,
                    name: dataNomineeDirector?.user_info.id_type?.name,
                }
                : undefined,
            NRIC : dataNomineeDirector.user_info
                ? dataNomineeDirector.user_info.NRIC
                : undefined,
            dateOfExpiry : dataNomineeDirector.user_info
                ? formatDate(dataNomineeDirector.user_info.date_of_expiry)
                : undefined,
            address : dataNomineeDirector.user_info
                ? dataNomineeDirector.user_info.address
                : undefined,
            DOB : dataNomineeDirector.user_info
                ? formatDate(dataNomineeDirector.user_info.DOB)
                : undefined,
            nationality: dataNomineeDirector?.user_info?.nationality
                ? {
                    id: dataNomineeDirector.user_info.nationality?.id,
                    nationality: dataNomineeDirector.user_info.nationality?.name,
                }
                : undefined,
            email : dataNomineeDirector.email,
            contact_number : dataNomineeDirector.user_info
                ? dataNomineeDirector.user_info.contact_number
                : undefined,
        });

        const fetchNomineeDirector = async() => {
            isFetchingData.value = true;
            nomineeDirectors.value = [];

            const nomineeDirectorLists = await NomineeDirector.all();

            nomineeDirectorLists.forEach(dataNomineeDirector => {
                const data = setNomineeDirector(dataNomineeDirector);
                nomineeDirectors.value.push(data);
            });

            isFetchingData.value = false;
        };

        fetchNomineeDirector();

        const handleNomineeDirectorCreateOrEdit = (toEditNomineeDirector=nomineeDirectorBluePrint, status="create") => {
            if(status === 'create') nomineeDirector.value = nomineeDirectorBluePrint;
            else nomineeDirector.value = toEditNomineeDirector.item;

            isNomineeDirectorHandlerSidebarActive.value = true;
        };

        const requestAutofillNomineeDirectorData = async id => {
            if (id) {
                const data = await User.getByNRIC(id);

                if(data)
                    autoFillRequestedNomineeDirector.value = data;

                else if(Object.keys(autoFillRequestedNomineeDirector.value).length)
                    autoFillRequestedNomineeDirector.value = {};
            }
        };

        return {
            nomineeDirector,
            nomineeDirectors,
            removeNomineeDirector,
            addNomineeDirector,
            updateNomineeDirector,
            clearNomineeDirectorData,
            fetchNomineeDirector,
            perfectScrollbarSettings,
            updateRouteQuery,

            // Click Handler
            handleNomineeDirectorCreateOrEdit,

            // Filters
            formatDate,
            avatarText,
            isFetchingData,

            // $refs
            table,

            isNomineeDirectorHandlerSidebarActive,
            isButtonActionLoading,
            requestAutofillNomineeDirectorData,
            autoFillRequestedNomineeDirector,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/base/pages/app-todo.scss";
</style>
